import type { ApiNewsPost } from "@/src/types/apinews";
import { Card, Divider, Elevation } from "@blueprintjs/core";
import { useCallback, useMemo, useState } from "react";
import NewsPost from "./newspost";
import styles from './news.module.css';
import { IconNames } from "@blueprintjs/icons";
import HomeSectionHeading from "./sectionheading";

type Props = {
    newsPosts: ApiNewsPost[];
}

export default function HomeNews({ newsPosts }: Props): JSX.Element {
    // create state for news posts so that we can remove posts from
    // the list when they are deleted without having to refresh the page
    const [newsPostsState, setNewsPostsState] = useState<ApiNewsPost[]>(newsPosts);

    const onDeletePost = useCallback(
        (newsPostId: number) => {
            setNewsPostsState((prev) => prev.filter((post) => post.id !== newsPostId));
        },
        [],
    );

    const components = useMemo(
        () => {
            const ret: JSX.Element[] = [];

            for (let i = 0; i < newsPostsState.length; i++) {
                const post = newsPostsState[i];

                ret.push(
                    <NewsPost
                        key={post.id}
                        title={post.title}
                        authorName={post.author.name}
                        authorAvatar={post.author.avatar}
                        date={post.createdAt}
                        content={post.content}
                        newsPostId={post.id}
                        onDeletePost={onDeletePost}
                    />
                );

                if (i !== newsPostsState.length - 1) {
                    ret.push(
                        <Divider
                            key={`divider-${i}`}
                            className={styles.divider}
                        />
                    );
                }
            }

            return ret;
        },
        [newsPostsState, onDeletePost],
    );

    if (!newsPostsState.length) {
        return null;
    }

    return (
        <>
            <HomeSectionHeading
                count={newsPostsState.length}
                icon={IconNames.Document}
                title="News"
            />
            <Card elevation={Elevation.ONE} className={styles.card}>
                {components}
            </Card>
        </>
    );
}
